import { UnitCalendarBounds } from "../services/unitCalendarBoundsService";

export const AGENDA_CONSOLIDATED_VIEW_STORAGE_KEY = "adatis.agenda.consolidatedView";

export interface CalendarSlotBounds {
  slotMinTime: string;
  slotMaxTime: string;
  scrollTime: string;
}

export const DEFAULT_CALENDAR_SLOT_BOUNDS: CalendarSlotBounds = {
  slotMinTime: "08:00:00",
  slotMaxTime: "20:00:00",
  scrollTime: "08:00:00",
};

export const readConsolidatedViewPreference = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(AGENDA_CONSOLIDATED_VIEW_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const writeConsolidatedViewPreference = (enabled: boolean): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(
      AGENDA_CONSOLIDATED_VIEW_STORAGE_KEY,
      enabled ? "true" : "false"
    );
  } catch {
    // ignore
  }
};

export const mergeCalendarSlotBounds = (
  bounds: UnitCalendarBounds[]
): CalendarSlotBounds => {
  if (bounds.length === 0) {
    return DEFAULT_CALENDAR_SLOT_BOUNDS;
  }

  let slotMinTime = bounds[0].slot_min_time;
  let slotMaxTime = bounds[0].slot_max_time;

  for (const item of bounds.slice(1)) {
    if (item.slot_min_time < slotMinTime) {
      slotMinTime = item.slot_min_time;
    }

    if (item.slot_max_time > slotMaxTime) {
      slotMaxTime = item.slot_max_time;
    }
  }

  return {
    slotMinTime,
    slotMaxTime,
    scrollTime: slotMinTime,
  };
};

export type ClosedCalendarDayMeta = {
  reason: string | null;
  exceptionId: number | null;
};

export const mergeClosedCalendarDates = (
  bounds: UnitCalendarBounds[]
): Set<string> => {
  const closedDates = new Set<string>();

  for (const item of bounds) {
    for (const day of item.days) {
      if (day.closed) {
        closedDates.add(day.date);
      }
    }
  }

  return closedDates;
};

/** Prefer the first non-empty exception reason/id across units for a closed day. */
export const mergeClosedCalendarDayMeta = (
  bounds: UnitCalendarBounds[]
): Map<string, ClosedCalendarDayMeta> => {
  const meta = new Map<string, ClosedCalendarDayMeta>();

  for (const item of bounds) {
    for (const day of item.days) {
      if (!day.closed) {
        continue;
      }

      const existing = meta.get(day.date);
      const reason =
        typeof day.closure_reason === "string" && day.closure_reason.trim() !== ""
          ? day.closure_reason.trim()
          : null;
      const exceptionId =
        typeof day.closure_exception_id === "number" ? day.closure_exception_id : null;

      if (!existing) {
        meta.set(day.date, { reason, exceptionId });
        continue;
      }

      if (!existing.reason && reason) {
        meta.set(day.date, {
          reason,
          exceptionId: exceptionId ?? existing.exceptionId,
        });
      } else if (!existing.exceptionId && exceptionId) {
        meta.set(day.date, {
          reason: existing.reason,
          exceptionId,
        });
      }
    }
  }

  return meta;
};
